import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { formatErr } from "@/lib/api";

const LOGO = "/assets/logo-nostradamus-oficial.png?v=3";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#0F1B2D] flex flex-col">
      {/* Top institutional bar */}
      <div className="bg-[#0F1B2D] text-white text-[11px] py-1.5 px-6 flex items-center justify-between tracking-wider uppercase font-mono">
        <span>Marinha do Brasil · Escola de Guerra Naval · LSC</span>
        <span className="hidden sm:inline">Plataforma Institucional · Acesso Restrito</span>
      </div>

      {/* Hero / Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-10 md:py-14 flex flex-col items-center text-center gap-5">
          <img
            src={LOGO}
            alt="Logotipo LSC/EGN"
            className="w-[120px] md:w-[160px] h-auto"
            data-testid="lsc-logo"
          />
          <div>
            <h1
              className="font-display font-bold text-4xl md:text-5xl text-[#0F1B2D] tracking-tight"
              data-testid="login-title"
            >
              Projeto Nostradamus
            </h1>
            <p className="mt-2 text-base md:text-lg text-slate-600 font-medium">
              Plataforma de Sementes de Futuro em Segurança Pública &amp; Defesa
            </p>
          </div>
          <p className="max-w-2xl text-sm md:text-base text-slate-500 leading-relaxed">
            Sistema de coleta, análise, classificação e visualização de fontes científicas
            e literatura cinzenta voltadas aos estudos prospectivos em Segurança Pública,
            Segurança Internacional e Defesa.
          </p>
          <div className="flex items-center gap-3 mt-1">
            <div className="h-px w-12 bg-[#C5A028]" />
            <span className="text-[10px] tracking-[0.3em] text-slate-400 uppercase font-mono">
              LSC · EGN · MB
            </span>
            <div className="h-px w-12 bg-[#C5A028]" />
          </div>
        </div>
      </header>

      {/* Login Card */}
      <main className="flex-1 flex items-start justify-center px-6 py-10">
        <div
          className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden"
          data-testid="login-card"
        >
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
            <h2 className="font-display font-bold text-[#0F1B2D] text-lg">Acessar a plataforma</h2>
            <p className="text-xs text-slate-500 mt-0.5">Identifique-se para continuar</p>
          </div>

          <div className="p-6 space-y-4">
            <div className="relative">
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="Disponível em breve"
                className="w-full border border-slate-200 rounded-md py-2.5 flex items-center justify-center gap-2 text-sm font-medium text-slate-400 bg-slate-50 opacity-60 cursor-not-allowed select-none"
                data-testid="google-login-button"
              >
                <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true" style={{ filter: "grayscale(100%)" }}>
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.3 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.3 6.1 29.5 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
                  <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5C29.6 34.6 26.9 35.5 24 35.5c-5.3 0-9.7-3.4-11.3-8L6.1 32.5C9.4 39.4 16.1 44 24 44z" />
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.6l6.5 5.5C41.9 36 44 30.6 44 24c0-1.3-.1-2.4-.4-3.5z" />
                </svg>
                Entrar com Google
                <span className="ml-1 inline-block text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Em breve</span>
              </button>
              <p className="text-[10px] text-slate-400 text-center mt-1.5">Integração futura — disponível em breve</p>
            </div>

            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] tracking-widest text-slate-400 uppercase font-mono">ou</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="login-form">
              <div>
                <label className="text-[11px] tracking-widest font-semibold uppercase text-slate-500 block mb-1.5">
                  E-mail institucional
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <input
                    type="email"
                    className="w-full bg-white border border-slate-300 rounded-md px-9 py-2 text-sm text-[#0F1B2D] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F]"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] tracking-widest font-semibold uppercase text-slate-500 block mb-1.5">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <input
                    type={showPwd ? "text" : "password"}
                    className="w-full bg-white border border-slate-300 rounded-md pl-9 pr-10 py-2 text-sm text-[#0F1B2D] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F]"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-[#1E3A5F]"
                    data-testid="login-password-toggle"
                    title={showPwd ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {err && (
                <div
                  className="text-sm text-red-700 border border-red-200 bg-red-50 p-3 rounded"
                  data-testid="login-error"
                >
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0F1B2D] hover:bg-[#1E3A5F] disabled:opacity-60 text-white font-semibold rounded-md py-2.5 text-sm tracking-wide transition-colors"
                data-testid="login-submit-button"
              >
                {loading ? "AUTENTICANDO…" : "ENTRAR"}
              </button>
            </form>

            <div className="pt-2 text-center">
              <Link
                to="/register"
                className="text-xs text-[#1E3A5F] hover:underline font-semibold"
                data-testid="register-link"
              >
                Solicitar acesso
              </Link>
            </div>

            <div
              className="mt-3 text-[11px] leading-relaxed text-slate-500 border-l-2 border-[#C5A028] bg-slate-50 p-3 rounded-r"
              data-testid="approval-notice"
            >
              O acesso ao sistema é liberado somente após aprovação manual de um
              administrador do Projeto Nostradamus.
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] text-slate-500 font-mono uppercase tracking-wider">
          <div>Laboratório de Simulações e Cenários · Escola de Guerra Naval</div>
          <div>Marinha do Brasil · {new Date().getFullYear()}</div>
        </div>
      </footer>
    </div>
  );
}
